import classNames from 'classnames';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { STAR, STAR_OUTLINE } from '@/components/switchback/Icon/assets';
import Icon from '@/components/switchback/Icon/IconComponent';

import css from './StarRating.module.css';

interface IStarRatingProps {
  className?: string;
  componentTestId?: string;
  disabledMin?: number;
  minRating: number;
  variant?: string;
  showRatingText?: boolean;
  iconSize?: number[];
  /**
   * When true, unselected stars render as an outline (transparent fill) instead
   * of the default filled-gray look. Opt-in so existing callers stay unchanged.
   */
  outlineForUnselected?: boolean;
  onChange?: React.ChangeEventHandler;
  onClick?: (e: React.MouseEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>) => void;
  starTestIdTemplate?: (value: number) => string;
  required?: boolean;
}

export enum Variant {
  yellow = 'text-yellow-800',
  green = 'text-gray-800',
}

const defaultIconSize = [24, 26];

const StarRating: React.FC<IStarRatingProps> = ({
  className = '',
  componentTestId,
  disabledMin = 0,
  minRating,
  variant = Variant.yellow,
  showRatingText = false,
  iconSize = defaultIconSize,
  outlineForUnselected = false,
  onChange,
  onClick,
  starTestIdTemplate,
  required,
}) => {
  const intl = useIntl();

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onClick && onClick(e);
      }
    },
    [onClick],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) onChange(e);
  };
  const ratingValues = ['TERRIBLE', 'BAD', 'OKAY', 'GOOD', 'GREAT'];

  const controlCss = classNames({
    // @ts-expect-error fixable: unchecked index access
    [css.controlGreen]: variant === Variant.green,
    // @ts-expect-error fixable: unchecked index access
    [css.controlYellow]: variant === Variant.yellow,
  });

  return (
    <fieldset
      data-testid={componentTestId || 'search-star-rating'}
      className={`inline-flex flex-row-reverse ${className}`}>
      {[1, 2, 3, 4, 5].reverse().map(value => {
        const testId = starTestIdTemplate ? starTestIdTemplate(value) : `search-${value}-stars`;

        return (
          <label className={css.label} data-testid={testId} key={`search-star-${value}`}>
            <input
              aria-label={intl.formatMessage(
                {
                  defaultMessage: '{value, plural, one {# star} other {# stars}}',
                  id: 'WPi3Lw',
                  description: 'components/route/search/StarRating label',
                },
                { value },
              )}
              className={`absolute opacity-0 pointer-events-none ${css.input}`}
              checked={value === minRating}
              disabled={value < disabledMin}
              name="search-star-rating"
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              onClick={onClick}
              type="radio"
              value={value}
              required={required}
            />
            <div className="flex-row">
              <span className={`${value <= minRating ? variant : 'text-gray-300'} ${controlCss}`}>
                <Icon
                  name={outlineForUnselected && value > minRating ? STAR_OUTLINE : STAR}
                  height={iconSize[0]}
                  width={iconSize[1]}
                />
              </span>
              {showRatingText && value === minRating && (
                <p className={`mt-1 text-xs font-medium text-center highlight ${variant}`}>
                  {ratingValues[value - 1]}
                </p>
              )}
            </div>
          </label>
        );
      })}
    </fieldset>
  );
};

StarRating.displayName = 'StarRating';

export default StarRating;
